<script setup lang="ts">
import type { BulletLegendItemInterface } from "@unovis/ts"
import type { Component } from "vue"
import { VisCrosshair, VisTooltip } from "@unovis/vue"
import { createApp } from "vue"
import { ChartTooltip } from "."
import { crosshairColor, crosshairTooltipRows } from "./crosshair-logic"

const props = withDefaults(defineProps<{
  colors: string[]
  index: string
  items: BulletLegendItemInterface[]
  customTooltip?: Component
}>(), {
  colors: () => [],
})

// Cache rendered tooltip HTML per datapoint to avoid re-mounting a Vue app on
// every hover frame. The cache is invalidated when the active-series set
// changes, so re-hovering a cached point after toggling a series still drops
// the toggled-off rows (N2).
const wm = new WeakMap<object, { sig: string; html: string }>()
function activeSignature() {
  return props.items.filter(i => i.inactive).map(i => i.name).join("|")
}
function template(d: any) {
  const sig = activeSignature()
  const cached = wm.get(d)
  if (cached && cached.sig === sig) {
    return cached.html
  }
  const componentDiv = document.createElement("div")
  // Only active series get a tooltip row (N2 fix lives in the pure helper).
  const omittedData = crosshairTooltipRows(d, props.index, props.items)
  const TooltipComponent = props.customTooltip ?? ChartTooltip
  createApp(TooltipComponent, { title: d[props.index].toString(), data: omittedData }).mount(componentDiv)
  wm.set(d, { sig, html: componentDiv.innerHTML })
  return componentDiv.innerHTML
}

function color(d: unknown, i: number) {
  return crosshairColor(props.colors, props.items, i)
}
</script>

<template>
  <VisTooltip :horizontal-shift="20" :vertical-shift="20" />
  <VisCrosshair :template="template" :color="color" />
</template>
